/**
 * File: src/pages/Dashboard/chat/Conversation.js
 * Description: Chat conversation panel with message list, quick replies,
 *              activity status changes, admin chat controls, and send actions.
 * Dependencies: React, Ant Design, Redux actions, custom components, network utils.
 * Guidelines:
 *  - Keep strings translatable where surfaced to UI.
 *  - Add JSDoc to non-trivial helpers and render builders.
 * Created: 2025-09-23
 * Last Updated: 2025-09-23
 */
 
import React, { useEffect, useRef, useState } from 'react'
import ChatData from "assets/data/chat.data.json"
import { Avatar, Divider, Input, Form, Button, Menu, Dropdown } from 'antd';
import {
	FileOutlined,
	SendOutlined,
	PaperClipOutlined,
	SmileOutlined,
	AudioMutedOutlined,
	UserOutlined,
	DeleteOutlined,
	EllipsisOutlined
} from '@ant-design/icons';
import { Scrollbars } from 'react-custom-scrollbars';
import Flex from 'components/shared-components/Flex';
import EllipsisDropdown from 'components/shared-components/EllipsisDropdown'
import { connect } from 'react-redux';
import { finishAdminChat, selectChatDetailList, selectChatList, startAdminChat, changeConversationActivityStatus, selectChatFilterOptions } from 'utils/redux/actions/chatActions';
import { bindActionCreators } from 'redux';
import actionTypes from 'utils/redux/actions/actionTypes';
import { MESSAGE_SEND_TYPE, MESSAGE_TYPES } from 'constants/messageConstants';
import { Spin, Alert, Select, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faList, faExternalLinkAlt, faChevronDown, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons'
import ListMessageItemModal from 'components/ListMessageItemModal';
import network from 'network/network';
import helperFunc from 'helpers/helperFunc';
import MessageItemInformationModal from 'components/MessageItemInformationModal';
import globalConstant from 'constants/globalConstant';
import { chatTrace } from 'utils/chatTrace';
import { getActivityStatusLabel, getActivityStatusOptions, getActivityStatusValue, hasVisibleActivity, normalizeActivityId, resolveConversationActivity } from './activityUtils';
const menuItems = [
	{
		key: '0',
		icon: <UserOutlined />,
		label: 'User Info',
	},
	{
		key: '1',
		icon: <AudioMutedOutlined />,
		label: 'Mute Chat',
	},
	{
		type: 'divider', // Menu.Divider yerine kullanılıyor
	},
	{
		key: '3',
		icon: <DeleteOutlined />,
		label: 'Delete Chat',
	},
];
const menu = () => (
	<Menu items={menuItems} />
);


/**
 * Conversation Component
 *
 * Displays the conversation thread, supports sending messages, showing
 * quick replies, message info, and transaction controls.
 *
 * @param {Object} props
 * @param {string|number} props.id - Active conversation id/number
 * @returns {JSX.Element}
 */
const Conversation = (props) => {

	const formRef = useRef();
	const chatBodyRef = useRef(null)
	const isInitial = useRef(true)
	const idIsChange = useRef(true)
	const messageDetailModalRef = useRef();

	const [listMessageId, setListMessageId] = useState(-1);
	const [messageHeader, setMessageHeader] = useState("");
	const [listItemModalVisible, setListItemModalVisible] = useState(false);
	const [adminMessage, setAdminMessage] = useState("");
	const [sendLoading, setSendLoading] = useState(false);
	const [messageInfoIconIndex, setMessageInfoIconIndex] = useState(false);
	const [quickReplies, setQuickReplies] = useState([]);
	const [quickRepliesLoading, setQuickRepliesLoading] = useState(false);
	const [productNameFromClientList, setProductNameFromClientList] = useState("");

	/**
	 * Inserts a selected quick reply into the admin message input.
	 *
	 * @param {string} reply - Predefined quick reply text
	 */
	const handleQuickReply = React.useCallback((reply) => {
		setAdminMessage(reply);
	}, []);

	const quickReplyMenu = React.useMemo(() => {
		const getMenuItems = () => {
			if (quickRepliesLoading) {
				return [
					{
						key: 'loading',
						label: 'Yükleniyor...',
						disabled: true
					}
				];
			}
			
			if (quickReplies.length === 0) {
				return [
					{
						key: 'empty',
						label: 'Hazır cevap bulunamadı',
						disabled: true
					}
				];
			}
			
			// Create items with bullet points and dividers
			const items = [];
			quickReplies.forEach((reply, index) => {
				// Add the menu item with bullet point
				items.push({
					key: index.toString(),
					label: (
						<span style={{ display: 'flex', alignItems: 'center' }}>
							<span style={{ 
								color: '#1890ff', 
								marginRight: 8,
								fontSize: 12,
								fontWeight: 'bold'
							}}>•</span>
							<span>{reply}</span>
						</span>
					),
					onClick: () => {
						handleQuickReply(reply);
					}
				});
				
				// Add divider after each item except the last one
				if (index < quickReplies.length - 1) {
					items.push({
						type: 'divider',
						key: `divider-${index}`
					});
				}
			});
			
			return items;
		};

		const menu = {
			items: getMenuItems()
		};
		
		return menu;
	}, [quickReplies, quickRepliesLoading, handleQuickReply]);

	// const { id } = props.match.params
	const { id } = props;

	const normalizeProductTextForView = React.useCallback((value) => {
		if (value === null || value === undefined) {
			return "";
		}

		const text = String(value).trim();
		if (!text) {
			return "";
		}

		const normalized = text.toLowerCase();
		const placeholderValues = [
			"urun bilgisi yok",
			"ürün bilgisi yok",
			"product info not found",
			"urun",
			"ürün",
			"product",
			"yok",
			"-",
			"n/a",
			"null",
			"undefined"
		];

		return placeholderValues.includes(normalized) ? "" : text;
	}, []);

	const normalizeCustomerNumberForLookup = React.useCallback((value) => {
		if (value === null || value === undefined) {
			return "";
		}

		return String(value).replace(/\D/g, "");
	}, []);

	const normalizeNameForLookup = React.useCallback((value) => {
		if (value === null || value === undefined) {
			return "";
		}

		return String(value).trim().toLowerCase();
	}, []);

	const shouldLogProductComparison = React.useCallback((value) => {
		const normalized = normalizeCustomerNumberForLookup(value);
		if (!normalized) {
			return false;
		}

		return normalized === "905051316886";
	}, [normalizeCustomerNumberForLookup]);

	const logProductSourceComparison = React.useCallback((payload) => {
		if (!payload?.enabled) {
			return;
		}

		const timestamp = new Date().toISOString();
		const label = `[Conversation][product-compare][${timestamp}] ${payload.customerNumber || "unknown"}`;
		console.groupCollapsed(label);
		console.groupEnd();

		chatTrace("conversation.product.compare", payload, "warn");
	}, []);


	const isSamePhoneForLookup = React.useCallback((left, right) => {
		const leftDigits = normalizeCustomerNumberForLookup(left);
		const rightDigits = normalizeCustomerNumberForLookup(right);

		if (!leftDigits || !rightDigits) {
			return false;
		}

		if (leftDigits === rightDigits) {
			return true;
		}

		const leftLast10 = leftDigits.length > 10 ? leftDigits.slice(-10) : leftDigits;
		const rightLast10 = rightDigits.length > 10 ? rightDigits.slice(-10) : rightDigits;

		return leftLast10 === rightLast10;
	}, [normalizeCustomerNumberForLookup]);

	const extractProductFromCustomerRecord = React.useCallback((customer) => {
		if (!customer || typeof customer !== "object") {
			return "";
		}

		const directValue = normalizeProductTextForView(
			customer.PRODUCTNAME ||
			customer.productname ||
			customer.productName ||
			customer.PRODUCTDESC ||
			customer.productdesc ||
			customer.productDesc ||
			customer.product ||
			customer.mainProductName ||
			customer.MAINPRODUCTNAME ||
			""
		);

		if (directValue) {
			return directValue;
		}

		const findProductLikeValue = (source, depth = 0) => {
			if (!source || depth > 2) {
				return "";
			}

			if (Array.isArray(source)) {
				for (const item of source) {
					const nested = findProductLikeValue(item, depth + 1);
					if (nested) {
						return nested;
					}
				}
				return "";
			}

			if (typeof source !== "object") {
				return "";
			}

			for (const [key, value] of Object.entries(source)) {
				if (typeof value === "string" && /product|urun/i.test(key)) {
					const normalized = normalizeProductTextForView(value);
					if (normalized) {
						return normalized;
					}
				}

				if (typeof value === "object" && value !== null) {
					const nested = findProductLikeValue(value, depth + 1);
					if (nested) {
						return nested;
					}
				}
			}

			return "";
		};

		const recursiveValue = findProductLikeValue(customer);
		if (recursiveValue) {
			return recursiveValue;
		}

		return "";
	}, [normalizeProductTextForView]);

	useEffect(() => {
		const currentProduct = normalizeProductTextForView(props.productName);
		const debugEnabled = shouldLogProductComparison(id);
		if (currentProduct && !debugEnabled) {
			setProductNameFromClientList("");
			return;
		}

		if (!id && !props.customerId) {
			setProductNameFromClientList("");
			return;
		}

		let isCancelled = false;

		const parseCustomerListData = (rawData) => {
			if (Array.isArray(rawData)) {
				return rawData;
			}

			if (typeof rawData === "string") {
				try {
					const parsed = JSON.parse(rawData);
					return Array.isArray(parsed) ? parsed : [];
				} catch (parseError) {
					return [];
				}
			}

			if (rawData && typeof rawData === "object") {
				if (Array.isArray(rawData.items)) {
					return rawData.items;
				}

				for (const value of Object.values(rawData)) {
					if (Array.isArray(value)) {
						return value;
					}
				}
			}

			return [];
		};

		const fetchProductFromCustomerList = async () => {
			try {
				const debugPayload = {
					enabled: debugEnabled,
					customerNumber: normalizeCustomerNumberForLookup(id),
					inputs: {
						customerId: props.customerId,
						customerName: props.customerName,
						messageDetailProduct: currentProduct || ""
					}
				};

				const getCustomerId = (customer) =>
					customer?.CUSTOMERID ||
					customer?.CLIENTID ||
					customer?.customerid ||
					customer?.clientid ||
					"";

				const getCustomerNumber = (customer) =>
					customer?.CUSTOMERNUMBER ||
					customer?.CLIENTNUMBER ||
					customer?.customernumber ||
					customer?.clientnumber ||
					customer?.customerNumber ||
					customer?.clientNumber ||
					customer?.sourcePhone ||
					"";

				const getCustomerName = (customer) =>
					customer?.CUSTOMERNAME ||
					customer?.CLIENTNAME ||
					customer?.customername ||
					customer?.clientname ||
					customer?.customerName ||
					customer?.clientName ||
					"";

				const fetchCustomerDetailById = async (customerId) => {
					if (!customerId) {
						return null;
					}

					try {
						const detailResult = await network.get("/admin/Client/selectClientDetail", { customerId }, false);
						if (detailResult.isSuccess && detailResult.data?.isSuccess && detailResult.data?.data) {
							return detailResult.data.data;
						}
					} catch (detailError) {
					}

					return null;
				};

				const idsEqual = (left, right) => {
					if (left === null || left === undefined || right === null || right === undefined) {
						return false;
					}

					return String(left).trim() === String(right).trim();
				};

				const fetchProductFromStampByClientId = async (customerId) => {
					if (!customerId) {
						return "";
					}

					try {
						const stampResult = await network.get("/admin/Stamp/GetAllStampByClient", {}, false);
						if (!stampResult.isSuccess || !stampResult.data?.isSuccess || !Array.isArray(stampResult.data?.data)) {
							return "";
						}

						const targetId = String(customerId).trim();
						const stamps = stampResult.data.data.filter((stamp) => {
							return idsEqual(stamp?.clientid, targetId) || idsEqual(stamp?.CLIENTID, targetId);
						});

						if (stamps.length === 0) {
							return "";
						}

						const activeStamp = stamps.find(stamp => stamp?.enddate == null || stamp?.ENDDATE == null) || stamps[0];
						return normalizeProductTextForView(
							activeStamp?.productname ||
							activeStamp?.PRODUCTNAME ||
							activeStamp?.productName ||
							""
						);
					} catch (stampError) {
						return "";
					}
				};

				const fetchProductFromFirmSettings = async () => {
					try {
						const settingsResult = await network.get("/admin/CoffeeSettings/GetSettings", null, false);
						if (!settingsResult?.isSuccess || !settingsResult.data?.isSuccess) {
							return "";
						}

						const settingsData = settingsResult.data?.data || settingsResult.data || {};
						return normalizeProductTextForView(
							settingsData?.mainproductname ||
							settingsData?.mainProductName ||
							settingsData?.MAINPRODUCTNAME ||
							settingsData?.productname ||
							settingsData?.productName ||
							settingsData?.PRODUCTNAME ||
							settingsData?.giftproductname ||
							settingsData?.giftProductName ||
							settingsData?.GIFTPRODUCTNAME ||
							""
						);
					} catch (settingsError) {
						return "";
					}
				};

				const [clientListResult, customersWithDetailResult] = await Promise.all([
					network.get("/admin/Client/selectClientList", {}, false),
					network.get("/admin/Client/GetCustomersWithDetail", {}, false)
				]);

				const clientList = (clientListResult.isSuccess && clientListResult.data?.isSuccess)
					? parseCustomerListData(clientListResult.data.data)
					: [];

				const customersWithDetail = (customersWithDetailResult.isSuccess && customersWithDetailResult.data?.isSuccess)
					? parseCustomerListData(customersWithDetailResult.data.data)
					: [];

				const mergedCustomerList = [...clientList, ...customersWithDetail];

				const targetCustomerId = props.customerId ? String(props.customerId) : "";
				const targetCustomerNumber = id;
				const targetCustomerName = normalizeNameForLookup(props.customerName);

				const customerMatchesId = (customer) => {
					if (!targetCustomerId) {
						return false;
					}

					const customerId = getCustomerId(customer);
					return idsEqual(customerId, targetCustomerId);
				};

				const customerMatchesPhone = (customer) => {
					if (!targetCustomerNumber) {
						return false;
					}

					const customerNumber = getCustomerNumber(customer);
					return !!customerNumber && isSamePhoneForLookup(targetCustomerNumber, customerNumber);
				};

				const customerMatchesName = (customer) => {
					if (!targetCustomerName) {
						return false;
					}

					const customerName = normalizeNameForLookup(getCustomerName(customer));
					return !!customerName && customerName === targetCustomerName;
				};


				let targetDetailedCustomer = null;
				let productFromTargetDetail = "";
				if (targetCustomerId) {
					targetDetailedCustomer = await fetchCustomerDetailById(targetCustomerId);
					productFromTargetDetail = extractProductFromCustomerRecord(targetDetailedCustomer);

					if (productFromTargetDetail && !debugEnabled) {
						if (!isCancelled) {
							setProductNameFromClientList(productFromTargetDetail);
						}
						return;
					}
				}

				const byIdCandidates = mergedCustomerList.filter(customerMatchesId);
				const byPhoneCandidates = mergedCustomerList.filter(customerMatchesPhone);
				const byNameCandidates = mergedCustomerList.filter(customerMatchesName);

				let matchedCustomer = null;
				let matchStrategy = "";
				if (byIdCandidates.length > 0) {
					matchedCustomer = byIdCandidates[0];
					matchStrategy = "id";
				} else if (targetCustomerName && targetCustomerNumber) {
					matchedCustomer = mergedCustomerList.find(customer => customerMatchesName(customer) && customerMatchesPhone(customer)) || null;
					matchStrategy = matchedCustomer ? "name+phone" : "";
				} else if (byNameCandidates.length > 0) {
					matchedCustomer = byNameCandidates[0];
					matchStrategy = "name";
				} else if (!targetCustomerName && byPhoneCandidates.length > 0) {
					matchedCustomer = byPhoneCandidates[0];
					matchStrategy = "phone";
				} else if (byPhoneCandidates.length === 1) {
					matchedCustomer = byPhoneCandidates[0];
					matchStrategy = "phone-single";
				}

				if (!matchedCustomer && targetDetailedCustomer) {
					matchedCustomer = targetDetailedCustomer;
					matchStrategy = matchStrategy || "detail";
				}

				let productFromMatchedRecord = extractProductFromCustomerRecord(matchedCustomer);
				let productFromMatchedDetail = "";

				if (!productFromMatchedRecord) {
					const matchedCustomerId = getCustomerId(matchedCustomer);
					if (matchedCustomerId) {
						const detailedCustomer = await fetchCustomerDetailById(matchedCustomerId);
						productFromMatchedDetail = extractProductFromCustomerRecord(detailedCustomer || matchedCustomer);
					}
				}

				let productFromStampByTargetId = "";
				if (!productFromMatchedRecord && !productFromMatchedDetail && targetCustomerId) {
					productFromStampByTargetId = await fetchProductFromStampByClientId(targetCustomerId);
				}

				let productFromStampByMatchedId = "";
				if (!productFromMatchedRecord && !productFromMatchedDetail && !productFromStampByTargetId) {
					const matchedCustomerId = getCustomerId(matchedCustomer);
					if (matchedCustomerId) {
						productFromStampByMatchedId = await fetchProductFromStampByClientId(matchedCustomerId);
					}
				}

				let productFromFirmSettings = "";
				if (!productFromMatchedRecord && !productFromMatchedDetail && !productFromStampByTargetId && !productFromStampByMatchedId) {
					productFromFirmSettings = await fetchProductFromFirmSettings();
				}

				const fallbackProductName =
					productFromTargetDetail ||
					productFromMatchedRecord ||
					productFromMatchedDetail ||
					productFromStampByTargetId ||
					productFromStampByMatchedId ||
					productFromFirmSettings ||
					"";

				debugPayload.match = {
					strategy: matchStrategy,
					candidateCounts: {
						byId: byIdCandidates.length,
						byPhone: byPhoneCandidates.length,
						byName: byNameCandidates.length
					},
					matchedCustomerId: getCustomerId(matchedCustomer) || "",
					matchedCustomerNumber: getCustomerNumber(matchedCustomer) || "",
					matchedCustomerName: getCustomerName(matchedCustomer) || ""
				};
				debugPayload.sources = {
					selectClientDetail: productFromTargetDetail || "",
					matchedRecord: productFromMatchedRecord || "",
					matchedDetail: productFromMatchedDetail || "",
					stampByTargetId: productFromStampByTargetId || "",
					stampByMatchedId: productFromStampByMatchedId || "",
					firmSettings: productFromFirmSettings || "",
					finalFallback: fallbackProductName || ""
				};
				logProductSourceComparison(debugPayload);

				if (!isCancelled) {
					setProductNameFromClientList(fallbackProductName || "");
				}
			} catch (error) {
				if (!isCancelled) {
					setProductNameFromClientList("");
				}
			}
		};

		fetchProductFromCustomerList();

		return () => {
			isCancelled = true;
		};
	}, [id, props.customerId, props.customerName, props.productName, extractProductFromCustomerRecord, isSamePhoneForLookup, normalizeNameForLookup, normalizeProductTextForView, shouldLogProductComparison, logProductSourceComparison, normalizeCustomerNumberForLookup]);

	useEffect(() => {
		//selectMessageList();
		selectMessageList();
		// Load activity options for the select dropdown
		if (props.selectChatFilterOptionsAction) {
			props.selectChatFilterOptionsAction();
		}
		// Load quick replies from API
		fetchQuickReplies();
	}, []);

	/**
	 * Fetches predefined quick reply messages from the API.
	 *
	 * @async
	 */
	const fetchQuickReplies = async () => {
		setQuickRepliesLoading(true);
		try {
			const result = await network.get("/admin/PredefinedMessage/getQuickReplies", null, false);
			
			if (result.isSuccess && result.data && result.data.isSuccess) {
				// Extract predefinedmessage field from each item (API returns lowercase)
				const dataArray = result.data.data || [];
				
				const messages = dataArray.map(item => {
					const message = item.predefinedmessage || 
						item.predefinedMessage || 
						item.PREDEFINEDMESSAGE || 
						'';
					return message;
				}).filter(msg => msg); // Filter out empty strings
				
				setQuickReplies(messages);
			} else {
				console.warn('Quick replies API response structure unexpected:', result);
				// Fallback to empty array if API fails
				setQuickReplies([]);
			}
		} catch (error) {
			console.error('Failed to fetch quick replies:', error);
			console.error('Error details:', error);
			// Fallback to empty array on error
			setQuickReplies([]);
		} finally {
			setQuickRepliesLoading(false);
		}
	};

	useEffect(() => {

		global.chatBodyRef = chatBodyRef.current;
	}, [chatBodyRef.current]);
	useEffect(() => {


		if (isInitial.current) {
			isInitial.current = false;

		} else {
			idIsChange.current = true;
			selectMessageList();

		}

	}, [id]);

	useEffect(() => {
		if (idIsChange.current) {
			idIsChange.current = false;
		} else
			if (props.scrollBottomActive) {
				scrollToBottom();
			}

	}, [props.scrollBottomActive]);
	/**
	 * Smoothly scrolls the chat body to the bottom.
	 */
	const scrollToBottom = () => {
		if (chatBodyRef.current) {
			setTimeout(() => {
				if (chatBodyRef.current) {
					chatBodyRef.current.scrollToBottom()
				}

			}, 100);

		}
	}

	/**
	 * Loads conversation messages for the active id.
	 *
	 * @param {boolean} [showLoading=true] - Whether to show loading indicator
	 */
	const selectMessageList = (showLoading = true) => {

		var params = {
			userToken: localStorage.getItem("auth_token"),
			customerNumber: id
		}
		props.selectChatListAction(params, showLoading);
	}


	/**
	 * Completes the admin conversation and refreshes related lists.
	 * Updates activity status to 4 before finishing the chat.
	 *
	 * @async
	 */
	const finishCustomerService = async () => {
		// Update activity status to 4 before finishing the chat
		try {
			await handleActivityStatusChange(4);
		} catch (error) {
			console.error('Failed to update activity status before finishing chat:', error);
			// Continue with finishing the chat even if activity update fails
		}

		var params = {
			customerNumber: id
		}
		await props.finishAdminChatAction(params);
		

		props.chatListAction(); // Sohbet listesini güncelle

		// Notify parent to reset active chat (triggers full re-render)
		if (props.onChatListRefresh) {
			props.onChatListRefresh();
		}
	}
	/**
	 * Shows confirmation before switching back to automated messaging.
	 */
	const finishCustomerServicePreview = () => {

		helperFunc.showConfirm({
			content: `Onaylıyor musunuz?`,
			title: "Sohbet Sonlandırılacaktır", onConfirm: () => finishCustomerService()
		});
	}

	/**
	 * Sends an admin text message to the active conversation.
	 * Updates chat list directly without fetching to avoid loading state.
	 *
	 * @async
	 */
	const onAdminSendMessage = async () => {

		if (adminMessage.trim().length === 0) {
			return;
		}

		var messageModel = {
			text: adminMessage,
			type: "text"
		}

		var params = {
			adminmessage: JSON.stringify(messageModel),
			message: (messageModel),
			destination: id,
			disablePreview: false,
			source: "",

		}
		setSendLoading(true)

		// Store the message text before clearing
		const messageText = adminMessage.trim();

		var result = await network.post("/messages/sendMessageFromAdmin", params);

		setSendLoading(false)
		if (result.isSuccess) {
			var postResult = result.data;
			if (postResult.isSuccess) {
				props.startAdminChatAction();
				params.sendType = 2;
				setAdminMessage("");

				// Get current date/time for the message
				const now = new Date();
				const messageDate = now.toLocaleDateString('tr-TR');
				const messageTime = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

				// Update chat list item directly without fetching (moves to top and updates last message)
				if (props.dispatch) {
					props.dispatch({
						type: actionTypes.CHAT_MASTER_UPDATE_PREV_TEXT,
						newText: messageText,
						customerNumber: id,
						activeNumber: id, // This is the active conversation, so don't increment unread
						messageDate: messageDate,
						messageTime: messageTime,
						moveToTop: true // Move this chat to the top of the list
					});
				}

				// Update activity status to 5 ("Sohbet Devam Ediyor") without triggering chat list fetch
				try {
					const isActiveUser = props.isAdminTransaction || props.isOpenTransaction;
					if (isActiveUser) {
						const activityParams = {
							customerNumber: id,
							activityId: 5,
							activityName: getActivityStatusLabel(5),
							customerId: props.customerId
						};
						// Pass skipChatListRefresh=true to prevent fetching
						await props.changeConversationActivityStatusAction(activityParams, true);
					} else {
						// For non-active users, use the existing flow
						await handleActivityStatusChange(5);
					}
				} catch (error) {
					console.error('Failed to update activity status after sending message:', error);
					// Don't show error to user as message was sent successfully
				}

			} else {
				alert(postResult.errorMessage);
			}

		} else {
			alert(result.errorMessage);
		}
	};

	/**
	 * Changes the conversation activity status and refreshes messages.
	 * Uses the transaction endpoint for both active and completed conversations.
	 *
	 * @param {number|string} activityId
	 * @async
	 */
	const handleActivityStatusChange = async (activityId) => {
		try {
			const normalizedActivityId = normalizeActivityId(activityId);
			if (normalizedActivityId === null) {
				return;
			}

			const activityName = getActivityStatusLabel(normalizedActivityId);
			if (!activityName) return;

			const isActiveUser = props.isAdminTransaction || props.isOpenTransaction;
			const params = {
				customerNumber: id,
				activityId: normalizedActivityId,
				activityName,
				customerId: props.customerId
			};

			// API aktif transaction yoksa son tamamlanmış transaction'a güvenli şekilde düşer.
			const result = await props.changeConversationActivityStatusAction(params, true);
			
			if (result && result.success) {
				message.success(`Aktivite durumu "${activityName}" olarak değiştirildi`);
				
				if (isActiveUser) {
					// Active users: refresh conversation details to show updated activity
					selectMessageList(false);
				}
			} else {
				message.error(result?.error || 'Aktivite durumu değiştirilemedi');
			}
		} catch (error) {
			console.error('Error in handleActivityStatusChange:', error);
			message.error('Aktivite durumu değiştirilirken hata oluştu');
		}
	};

	/**
	 * Renders the canonical activity status Select for all conversations.
	 *
	 * @returns {JSX.Element|null}
	 */
	const getActivityStatusSelect = () => {
		const activityOptions = getActivityStatusOptions();

		return (
			<div style={{ marginBottom: 16 }}>
				<div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Aktivite Durumu:</div>
				<Select
					style={{ width: '100%' }}
					placeholder="Aktivite seçin"
					value={getActivityStatusValue(props.activityId, props.activityName)}
					onChange={handleActivityStatusChange}
					options={activityOptions}
				/>
			</div>
		);
	};

	/**
	 * Renders the "Sohbeti Bitir" action for admin conversations.
	 *
	 * @returns {JSX.Element|undefined}
	 */
	const getFinishTransactionButton = () => {

		if (props.isAdminTransaction) {

			return (
				<Button type='primary' onClick={() => finishCustomerServicePreview()} htmlType="button"  >
					Sohbeti Bitir
				</Button>
			);
		}
	}


	/**
	 * Renders chat header with customer avatar, name and actions.
	 *
	 * @param {string} name - Customer name
	 * @returns {JSX.Element}
	 */
	const chatContentHeader = name => (
		<div className="chat-content-header" style={{ background: "#fff" }}>
			<div style={{ display: "flex", justifyContent: "flex-start", flexDirection: "row", alignItems: "center" }}>

				<img style={{ borderRadius: 40, width: 35, height: 35, marginRight: 15 }} src={globalConstant.imageTypes.getImageUrl(globalConstant.imageTypes.customerImage.type, props.customerImage)}></img>
				<h4 className="mb-0">{name}</h4>
			</div>

			<Flex>
				<div>
					{getFinishTransactionButton()}
				</div>
			</Flex>
		</div>
	)




	/**
	 * Renders a quick-reply style message bubble with buttons.
	 *
	 * @param {Object} item - Message item with content and options
	 * @param {number} i - Index
	 * @returns {JSX.Element}
	 */
	const getQuickRepplyMessage = (item, i) => {
		// Bubble with message content
		const bubble = (
			<div style={bubbleStyle(item.sendType === MESSAGE_SEND_TYPE.outbound)}>
				{item.sendType === MESSAGE_SEND_TYPE.outbound && item.firmUserName && (
					<div style={{ fontSize: 11, color: "#888", fontWeight: "bold", marginBottom: 2 }}>
						{item.firmUserName.split('@')[0]}
					</div>
				)}
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
					<div style={{ flex: 1 }}>
						<div className='header'>
							<span className='bold' style={{ fontWeight: "bold" }}>{item.message.content.header}</span>
						</div>
						<div className='content' style={{ marginTop: 5, marginBottom: 5 }}>
							<span className=''>{item.message.content.text}</span>
						</div>
						<div className='' style={{ color: "gray" }}>
							<span className=''>{item.message.content.caption}</span>
						</div>
					</div>
					{getMessageDateInline(item)}
				</div>
			</div>
		);

		// Buttons outside the bubble
		const buttons = (
			<div style={{ marginLeft: item.sendType === MESSAGE_SEND_TYPE.outbound ? 'auto' : 0, marginRight: item.sendType === MESSAGE_SEND_TYPE.outbound ? 0 : 'auto', maxWidth: 260 }}>
				{item.message.options.map((item2, idx) => (
					<div key={idx} style={{
						marginTop: idx === 0 ? 8 : 6,
						marginBottom: 0,
						width: '100%',
						background: item.sendType === MESSAGE_SEND_TYPE.outbound ? '#d2f8c6' : '#f5f5f5',
						color: '#222',
						border: '1px solid #c8c8c8',
						borderRadius: 12,
						padding: '7px 0',
						textAlign: 'center',
						fontWeight: 500,
						fontSize: 13,
						cursor: 'pointer',
						display: 'block',
					}}>
						{item2.title}
					</div>
				))}
			</div>
		);

		return (
			<div key={item.mainMessageId}>
				{bubble}
				{buttons}
			</div>
		);
	}


	/**
	 * Builds dropdown menu for a message (e.g., show details).
	 *
	 * @param {Object} item - Message item
	 * @returns {JSX.Element|undefined}
	 */
	const getMessageDropdownMenu = (item) => {

		if (item.sendType === MESSAGE_SEND_TYPE.inbound) {

			return;
		}
		return (
			<Menu>
				<Menu.Item key="0">
					<a onClick={() => {
						messageDetailModalRef.current.getMessageInfo(item.mainMessageId)
					}}>Mesaj Detayı</a>
				</Menu.Item>

			</Menu>
		);
	}
	/**
	 * Renders per-message info icon and dropdown when active.
	 *
	 * @param {Object} message - Message item
	 * @param {number} index - Message index
	 * @returns {JSX.Element|null|undefined}
	 */
	const getMessageInfoIcon = (message, index) => {

		return null;
		if (message.sendType === MESSAGE_SEND_TYPE.inbound) {
			return;
		}
		if (messageInfoIconIndex == index) {
			return (
				<Dropdown menu={getMessageDropdownMenu(message)} trigger={['click', 'contextMenu']}>
					<a style={{ cursor: "pointer", float: "right" }}
					>
						<FontAwesomeIcon style={{ marginRight: 5, color: "#585858", fontSize: 16 }} icon={faChevronDown} />
					</a>
				</Dropdown>
			);
		}

	}

	/**
	 * Renders formatted message date/time inline.
	 *
	 * @param {Object} elm - Message item containing messageDate/Time
	 * @returns {JSX.Element}
	 */
	const getMessageDate = (elm) => {


		return (
			<div style={{ textAlign: "right" }}>
				<label style={{ fontSize: 11 }}>
					{elm.messageDate ? elm.messageDate + " - " : ""}
					{elm.messageTime}
				</label>
			</div>
		);
	}

	// WhatsApp-like chat bubble style
	/**
	 * Returns style object for chat bubble depending on direction.
	 *
	 * @param {boolean} isOutbound - True if message is outbound
	 * @returns {Object}
	 */
	const bubbleStyle = (isOutbound) => ({
		maxWidth: 260, // reduce width
		margin: '6px 0', // less vertical space
		padding: '2px 5px', // less padding
		borderRadius: isOutbound ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
		background: isOutbound ? '#d2f8c6' : '#fff',
		alignSelf: isOutbound ? 'flex-end' : 'flex-start',
		boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
		color: '#222',
		fontSize: 13, // slightly smaller text
		border: '1px solid #ececec',
		wordBreak: 'break-word',
		minWidth: "60px"
	});

	/**
	 * Renders compact inline date/time for a message bubble.
	 *
	 * @param {Object} elm - Message item
	 * @returns {JSX.Element}
	 */
	const getMessageDateInline = (elm) => {
		return (
			<span style={{ fontSize: 10, color: "#aaa", marginLeft: 8, whiteSpace: 'nowrap' }}>
				{elm.messageDate ? elm.messageDate + " - " : ""}
				{elm.messageTime}
			</span>
		);
	}

	/**
	 * Renders chat message based on its type (text, location, quick reply, list).
	 *
	 * @param {Object} elm - Message item
	 * @param {number} i - Index
	 * @returns {JSX.Element[]}
	 */
	const getChatText = (elm, i) => {
		var messageObject = [];
		const messageType = (elm.message || {}).type;
		switch (messageType) {
			case MESSAGE_TYPES.TEXT:
				var messsageText = elm.message.text;
				var expression = /(https?:\/\/(?:www\.|(?!www))[^\s\.]+\.[^\s]{2,}|www\.[^\s]+\.[^\s]{2,})/gi;
				var matches = (messsageText || "").match(expression);
				if (matches && matches.length > 0) {
					for (let i = 0; i < matches.length; i++) {
						const element = matches[i];
						var linkStr = `<a target="_blank" href = "${element}"> ${element} </a>`;
						messsageText = messsageText.replace(element, linkStr);
					}
				}
				// Inline date/time for text messages
				messageObject = [
					<div key={elm.mainMessageId} style={bubbleStyle(elm.sendType === MESSAGE_SEND_TYPE.outbound)}>
						{/* Show admin name for outbound messages */}
						{elm.sendType === MESSAGE_SEND_TYPE.outbound && elm.firmUserName && (
							<div style={{ fontSize: 11, color: "#888", fontWeight: "bold", marginBottom: 2 }}>
								{elm.firmUserName.split('@')[0]}
							</div>
						)}
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
							<div style={{ flex: 1 }}>
								<span dangerouslySetInnerHTML={{ __html: messsageText }} />
							</div>
							{getMessageDateInline(elm)}
						</div>
					</div>
				]
				break;
			case MESSAGE_TYPES.IMAGE:

				break;
			case MESSAGE_TYPES.LOCATION:

				messageObject = [
					<div key={elm.mainMessageId} className={`bubble-wrapper${elm.sendType === MESSAGE_SEND_TYPE.outbound ? "me" : ""}`}
					>
						<a target={"_blank"} href={`https://www.google.com/maps/search/?api=1&query=${elm.message.latitude},${elm.message.longitude}`}>
							<FontAwesomeIcon style={{ marginRight: 5, color: "#585858", fontSize: 16 }} icon={faMapMarkerAlt} />
							Konumu görüntülemek için Tıklayın
						</a>
						{/* Date at bottom right for non-text messages */}
						<div style={{ textAlign: "right" }}>
							<span style={{ fontSize: 10, color: "#aaa", margin: 0, padding: 0 }}>
								{elm.messageDate ? elm.messageDate + " - " : ""}
								{elm.messageTime}
							</span>
						</div>
					</div>
				]
				break;
			case MESSAGE_TYPES.QUICK_REPPLY:
				messageObject = [getQuickRepplyMessage(elm, i)]
				break;
			case MESSAGE_TYPES.LIST:
				messageObject = [
					<div key={elm.mainMessageId} style={bubbleStyle(elm.sendType === MESSAGE_SEND_TYPE.outbound)}>
						{/* Show admin name for outbound messages */}
						{elm.sendType === MESSAGE_SEND_TYPE.outbound && elm.firmUserName && (
							<div style={{ fontSize: 11, color: "#888", fontWeight: "bold", marginBottom: 2 }}>
								{elm.firmUserName.split('@')[0]}
							</div>
						)}
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
							<div style={{ flex: 1 }}>
								<div className='header'>
									<span className='bold' style={{ fontWeight: "bold" }}>{elm.message.title}</span>
								</div>
								<div className='content' style={{ marginTop: 5, marginBottom: 5 }}>
									<span className=''>{elm.message.body}</span>
								</div>
							</div>
							{getMessageDateInline(elm)}
						</div>
						<div onClick={() => {
							setListMessageId(elm.mainMessageId);
							setListItemModalVisible(true);
							setMessageHeader(elm.message.globalButtons[0].title);
						}} style={{ 
							marginTop: 8,
							marginBottom: 0,
							width: '100%',
							background: elm.sendType === MESSAGE_SEND_TYPE.outbound ? '#c5e5b4' : '#efefef',
							color: '#222',
							border: '1px solid #c8c8c8',
							borderRadius: 8,
							padding: '7px 0',
							textAlign: 'center',
							fontWeight: 500,
							fontSize: 13,
							cursor: 'pointer',
							display: 'block',
						}}>
							<FontAwesomeIcon style={{ marginRight: 5, }} className="text-primary" icon={faList} />
							{elm.message.globalButtons[0].title}
						</div>
					</div>
				]
				break;
			case MESSAGE_TYPES.BUTTON_REPPLY:


				break;
			case MESSAGE_TYPES.LIST_REPPLY:

				break;
			case MESSAGE_TYPES.FILE:

				messageObject = [

					<div key={elm.mainMessageId} className={`bubble-wrapper${elm.sendType === MESSAGE_SEND_TYPE.outbound ? "me" : ""}`}>
						<div style={{
							minWidth: 120,
							padding: 10,
							paddingBottom: 5,
							paddingTop: 5,
							fontWeight: "bold",
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "space-between",
							background: "#d3eebe",
							borderRadius: 10,
							cursor: "pointer"
						}}
							onClick={() => {
								window.open(elm.message.url, "_blank");
							}}
						>

							{elm.message.filename || "Dosya"}
							<FontAwesomeIcon style={{ marginRight: 5, }} className="text-primary" icon={faExternalLinkAlt} />
						</div>
						{/* Date at bottom right for non-text messages */}
						<div style={{ textAlign: "right" }}>
							<span style={{ fontSize: 10, color: "#aaa", margin: 0, padding: 0 }}>
								{elm.messageDate ? elm.messageDate + " - " : ""}
								{elm.messageTime}
							</span>
						</div>
					</div>

				]

				break;

			default:
				break;
		}

		return (
			<div
				key={`msg-${id}-${i}`}
				className={`msg ${elm.msgType === 'date' ? 'datetime' : ''} ${elm.sendType === MESSAGE_SEND_TYPE.inbound ? 'msg-recipient' : elm.sendType === MESSAGE_SEND_TYPE.outbound ? 'msg-sent mr-4' : ''}`}
				style={{ whiteSpace: "pre-wrap", marginBottom: "0px" }}
			>
				<div className={`bubble ml-5`}>

					{messageObject}
				</div>
				{getMessageInfoIcon(elm, i)}
			</div>
		);
	}


	const chatContentBody = (props, id) => (
		<div className="chat-content-body" style={{ padding: 0 }} >
			<Scrollbars ref={chatBodyRef} autoHide>
				{
					props.map((elm, i) => (
						<React.Fragment key={elm.mainMessageId || `msg-${id}-${i}`}>
							{getChatText(elm, i)}
						</React.Fragment>
					))
				}
			</Scrollbars>
		</div>
	)

	const chatContentFooter = () => {
		const isActiveUser = props.isAdminTransaction || props.isOpenTransaction;
		// Aktif işlem yoksa mesaj yazma alanını gösterme
		if (!isActiveUser) {
			return (
				<div style={{
					background: '#fff',
					borderTop: '1px solid #f0f0f0',
					padding: '18px 24px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					boxShadow: '0 -2px 8px rgba(0,0,0,0.03)',
					color: '#888',
					fontSize: 14
				}}>
					Bu müşteri aktif değil. Mesaj gönderemezsiniz.
				</div>
			);
		}

		return (
			<div style={{
				background: '#fff',
				borderTop: '1px solid #f0f0f0',
				padding: '18px 24px',
				display: 'flex',
				alignItems: 'center',
				boxShadow: '0 -2px 8px rgba(0,0,0,0.03)'
			}}>
				<Input.TextArea
					value={adminMessage}
					onChange={e => setAdminMessage(e.target.value)}
					onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); onAdminSendMessage(); } }}
					placeholder="Mesajınızı yazın..."
					autoSize={{ minRows: 1, maxRows: 4 }}
					style={{ borderRadius: 16, resize: 'none', marginRight: 12, fontSize: 15 }}
				/>
				<Dropdown
					menu={quickReplyMenu}
					trigger={['click']}
					placement="topRight"
				>
					<Button
						type="default"
						shape="circle"
						icon={<EllipsisOutlined />}
						style={{ marginRight: 8,width:"40px" }}
						title="Hazır Cevaplar"
						

					/>
				</Dropdown>
				<Button
					type="primary"
					shape="circle"
					icon={<SendOutlined />}
					loading={sendLoading}
					onClick={onAdminSendMessage}
					style={{ width: "40px" }}
				/>
			</div>
		);
	}

	const getContent = () => {


		if (props.isLoading) {

			return (
				<Spin tip="Mesajlar Yükleniyor..." style={{ width: "100%", height: "100%", marginTop: 25 }}>
				</Spin>
			);
		} else {

			return (
				<>
					{chatContentHeader(props.customerName)}
					{chatContentBody(props.messageList, id)}
					{chatContentFooter()}
				</>
			);
		}


	}


	return (
		<div style={{ display: "flex", height: "100%" }}>
			{/* Chat Area */}
			<div
				className="chat-content"
				style={{
					flex: 1,
					backgroundColor: "#e5ddd5",
					backgroundImage: "url(https://s3-us-west-2.amazonaws.com/s.cdpn.io/1089577/background.png)",
					backgroundRepeat: "repeat",
					backgroundSize: "auto",
					display: "flex",
					flexDirection: "column",
					justifyContent: "space-between",
				}}
			>
				<ListMessageItemModal messageId={listMessageId} modalVisible={listItemModalVisible} setModalVisible={(isVisible) => {
					setListItemModalVisible(isVisible);
					setListMessageId(-1);
				}} messageHeader={messageHeader}></ListMessageItemModal>

				<MessageItemInformationModal ref={messageDetailModalRef}>

				</MessageItemInformationModal>
				{getContent()}
			</div>

			{/* User Info Panel */}
			<div
				style={{
					width: 300,
					background: "#fff",
					color: "#222",
					padding: 24,
					borderLeft: "1px solid #eee",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}
			>
				<div style={{ position: 'relative', marginBottom: 16 }}>
					<img
						src={globalConstant.imageTypes.getImageUrl(globalConstant.imageTypes.customerImage.type, props.customerImage)}
						alt="User"
						style={{ width: 80, height: 80, borderRadius: "50%" }}
					/>
					{/* İşlem Durumu Belirteci */}
					<div
						style={{
							position: 'absolute',
							top: 4,
							right: 4,
							width: 16,
							height: 16,
							borderRadius: '50%',
							backgroundColor: props.isAdminTransaction ? '#52c41a' : (props.isOpenTransaction ? '#ff9603' : '#d9d9d9'),
							border: '3px solid #fff',
							boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
						}}
						title={props.isOpenAdmin ? 'Admin Aktif' : (props.isOpenTransaction ? 'Aktif İşlem' : 'Pasif İşlem')}
					/>
				</div>
				<h3 style={{ color: "#222" }}>{props.customerName}</h3>
				<div style={{ marginTop: 8, fontSize: 14 }}>
					<div><b>Telefon:</b> {id}</div>
					<div><b>Ürün:</b> {props.productName || productNameFromClientList || "-"}</div>
					{hasVisibleActivity(props.activityId, props.activityName) && (
						<div style={{ marginTop: 8, padding: 8, backgroundColor: '#f0f8ff', borderRadius: 6, border: '1px solid #1890ff' }}>
							<div style={{ fontSize: 12, color: '#1890ff', fontWeight: 'bold', marginBottom: 4 }}>
								🏷️ Son Aktivite
							</div>
							<div style={{ color: '#222', fontWeight: 'bold' }}>
								{getActivityStatusLabel(props.activityId, props.activityName)}
							</div>
						</div>
					)}
					<div style={{ marginTop: 12 }}>
						{getActivityStatusSelect()}
					</div>
				</div>
			</div>
		</div>
	)

}

/**
 * Maps Redux state to component props for conversation view.
 *
 * @param {Object} state - Redux root state
 * @returns {Object}
 */
export function mapStateToProps(state, ownProps) {
	const normalizeProductName = (value) => {
		if (value === null || value === undefined) {
			return "";
		}

		const productText = String(value).trim();
		if (!productText) {
			return "";
		}

		const normalized = productText.toLowerCase();
		const placeholderValues = [
			"urun bilgisi yok",
			"ürün bilgisi yok",
			"product info not found",
			"urun",
			"ürün",
			"product",
			"yok",
			"-",
			"n/a",
			"null",
			"undefined"
		];

		if (placeholderValues.includes(normalized)) {
			return "";
		}

		return productText;
	};

	const normalizeCustomerNumber = (value) => {
		if (value === null || value === undefined) {
			return "";
		}

		return String(value).replace(/\D/g, "");
	};

	const getProductNameFromChat = (chat) => {
		if (!chat) {
			return "";
		}

		const findProductLikeValue = (source, depth = 0) => {
			if (!source || typeof source !== "object" || depth > 2) {
				return "";
			}

			for (const [key, value] of Object.entries(source)) {
				if (value === null || value === undefined) {
					continue;
				}

				if (typeof value === "string" && /product|urun/i.test(key)) {
					const normalized = normalizeProductName(value);
					if (normalized) {
						return normalized;
					}
				}

				if (typeof value === "object") {
					const nested = findProductLikeValue(value, depth + 1);
					if (nested) {
						return nested;
					}
				}
			}

			return "";
		};

		return normalizeProductName(
			chat.productName ||
			chat.productname ||
			chat.PRODUCTNAME ||
			chat.product ||
			chat.productDesc ||
			chat.productdesc ||
			chat.PRODUCTDESC ||
			chat.mainProductName ||
			chat.MAINPRODUCTNAME ||
			findProductLikeValue(chat) ||
			""
		);
	};

	// Get activityId from chat list as fallback for non-active users
	// (conversation detail returns 0 for non-active users since there's no transaction)
	const chatList = state.chatReducer.chatList || [];
	const selectedCustomerNumber = ownProps?.id || state.chatDetailReducer.customerNumber;
	const normalizedSelectedNumber = normalizeCustomerNumber(selectedCustomerNumber);
	const normalizedDetailNumber = normalizeCustomerNumber(state.chatDetailReducer.customerNumber);
	const targetCustomerNumber = normalizedSelectedNumber || normalizedDetailNumber;
	const currentChat = chatList.find(chat => {
		const chatNumber = normalizeCustomerNumber(chat?.customerNumber);
		return Boolean(chatNumber && targetCustomerNumber && chatNumber === targetCustomerNumber);
	});
	const activityIdFromChatList = currentChat?.activityId;
	const activityNameFromChatList = currentChat?.activityName
		|| currentChat?.activityDesc
		|| currentChat?.activitydesc
		|| '';
	const productNameFromChatList = getProductNameFromChat(currentChat);
	
	// Conversation detail can return the legacy value 0 for passive users. In that
	// case, resolve the selected activity from the matching chat-list record.
	const conversationActivityId = state.chatDetailReducer.activityId;
	const conversationProductName = normalizeProductName(state.chatDetailReducer.productName);
	const resolvedActivity = resolveConversationActivity({
		detailActivityId: conversationActivityId,
		detailActivityName: state.chatDetailReducer.activityName,
		listActivityId: activityIdFromChatList,
		listActivityName: activityNameFromChatList
	});
	const finalProductName = conversationProductName || productNameFromChatList;
	return {
		isLoading: state.chatDetailReducer.isLoading,
		messageList: state.chatDetailReducer.messageList,
		hasError: state.chatDetailReducer.hasError,
		errorMessage: state.chatDetailReducer.errorMessage,
		customerName: state.chatDetailReducer.customerName,
		customerId: state.chatDetailReducer.customerId,
		customerImage: state.chatDetailReducer.customerImage,
		scrollBottomActive: state.chatDetailReducer.scrollBottomActive,
		isAdminTransaction: state.chatDetailReducer.isAdminTransaction,
		productName: finalProductName,
		activityName: resolvedActivity.activityName,
		activityId: resolvedActivity.activityId,
		isOpenTransaction:state.chatDetailReducer.isOpenTransaction,
		isOpenAdmin:state.chatDetailReducer.isOpenAdmin,
		filterOptions: state.chatReducer.filterOptions,
		filterOptionsLoading: state.chatReducer.filterOptionsLoading,
		filterOptionsError: state.chatReducer.filterOptionsError,
		filterOptionsErrorMessage: state.chatReducer.filterOptionsErrorMessage,
	}
}

/**
 * Binds conversation-related Redux actions to props.
 *
 * @param {Function} dispatch - Redux dispatch
 * @returns {Object}
 */
function mapDispatchToProps(dispatch) {
	return {
		selectChatListAction: bindActionCreators(selectChatDetailList, dispatch),
		finishAdminChatAction: bindActionCreators(finishAdminChat, dispatch),
		startAdminChatAction: bindActionCreators(startAdminChat, dispatch),
		chatListAction: bindActionCreators(selectChatList, dispatch),
		changeConversationActivityStatusAction: bindActionCreators(changeConversationActivityStatus, dispatch),
		selectChatFilterOptionsAction: bindActionCreators(selectChatFilterOptions, dispatch),
		dispatch: dispatch // Add dispatch to props for manual action dispatching
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(Conversation);
